import { useEffect, useRef } from 'react';
import { useBoardWriteAccess } from '../../hooks/boardWriteAccess';
import type { AccessoryFormApi } from './useAccessoryForm';

/**
 * KA — ACCESS DOOR "To Floor" minimum clearance (Data Bar → Accessories →
 * Access Door → Mounting).
 *
 * The server already computes this floor and only COMPLAINS about it:
 * `AHUAccessoryAccessDoor.min_clear_from_floor`
 * (django/ahu/models_accessory_access_door.py:533-564) is
 *
 *   side wall (wall_left | wall_right):
 *     max(min_clear_if_on_side_wall_from_flr, min_clear_if_on_side_wall_from_btm)
 *   end wall  (wall_upstream | wall_downstream):
 *     max(min_clear_if_on_end_wall_from_flr,  min_clear_if_on_end_wall_from_btm)
 *
 * and `clear_from_floor_validation` turns `min_clear - position.y` into the
 * `error_min_clr_flr` Error-Manager row ("Move the access door up…"). This
 * module mirrors THAT rule — nothing else — so the user cannot enter the value
 * that produces the error in the first place. BOTH boxes that write this number
 * carry the bound: the Data Bar's To Floor (`complexMounting`'s `PositionInput`,
 * bound to the door's SAVED plane) and the mounting popup's `distance_to_floor`
 * (`AccessoryMountingPopup`'s `DistInput`, which `distancesToPosition` maps onto
 * the same `position.y`, bound to the plane STAGED in the popup — see
 * `floorMinFor`).
 *
 * The numbers come from the API, never from code: the access-door detail
 * response carries `clearances` (AHUAccessory.clearances → model_to_dict of
 * `cms_clearances`, api/rest_ahu/access_door_serializers.py:67), and
 * `cms_clearances` already has the construction class's
 * `door_min_clear_if_on_{side,end}_wall_from_flr` written over the CMS mfg row
 * (models_accessory_access_door.py:224-238) — so e.g. mfg 1 under class 1 is
 * `max(10, 15) = 15` on a side wall and `max(7, 20) = 20` on an end wall, which
 * is what `min_clear_from_floor` returns for the same door.
 *
 * Parity note: every `null` branch of the server property is reproduced as "no
 * minimum" (no position, no plane, no cms row, a non-`unique` partner door, a
 * missing `*_from_flr`/`*_from_btm`) — where the server declines to validate,
 * this declines to constrain.
 */

/** side/end wall → the pair of `clearances` keys the server maxes. */
const FLOOR_CLEARANCE_KEYS: Record<string, [string, string]> = {
  wall_left: ['min_clear_if_on_side_wall_from_flr', 'min_clear_if_on_side_wall_from_btm'],
  wall_right: ['min_clear_if_on_side_wall_from_flr', 'min_clear_if_on_side_wall_from_btm'],
  wall_upstream: ['min_clear_if_on_end_wall_from_flr', 'min_clear_if_on_end_wall_from_btm'],
  wall_downstream: ['min_clear_if_on_end_wall_from_flr', 'min_clear_if_on_end_wall_from_btm'],
};

/**
 * The minimum To Floor value for a door on `planeValue`, or null when the
 * server would not validate it either. `planeValue` is the resolved on-cabinet
 * plane value (roof/floor have no To Floor row; an internal wall measures "To
 * Bottom Edge" instead and is not covered by the server rule).
 */
export function minToFloorClearance(clearances: unknown, planeValue: string | null): number | null {
  if (!planeValue || !clearances || typeof clearances !== 'object') return null;
  const keys = FLOOR_CLEARANCE_KEYS[planeValue];
  if (!keys) return null;
  const row = clearances as Record<string, unknown>;
  const values: number[] = [];
  for (const key of keys) {
    const raw = row[key];
    if (raw == null || raw === '') return null; // server logs + skips the check
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    values.push(n);
  }
  return Math.max(...values);
}

/**
 * `AHUUnit.validate_value` (`django/ahu/models_unit.py:565`) raises no error
 * for a shortfall under 0.1, so a door 0.05 below the minimum has no
 * Error-Manager row. The load-time correction uses the same threshold — it
 * exists to clear that row, and rewriting saved geometry the server is content
 * with would be churn. A value the USER types is still clamped strictly: the
 * box shows a minimum, so it may not accept less than it.
 */
const SERVER_TOLERANCE = 0.1;

/**
 * The minimum for the door this form holds, plus the load-time correction:
 * a stored To Floor below the minimum is raised TO the minimum, on a board code
 * is allowed to write to unprompted — `BoardWriteAccessContext`, whose docblock
 * lists what that excludes (every one of those states is CSS only on the client
 * and accepted by the server, archived projects included).
 *
 * The correction WRITES, because `position.y` is the number the drawing, the
 * CAD config and the reports all read; a UI-only correction would show 15.00
 * while the unit was still built with the door on the floor. It is a no-op for
 * a door that is already legal, and it can only ever move a door UP to a
 * CMS-derived floor.
 *
 * It goes through `commitPositionField(..., { positionOnly: true })`: the same
 * PATCH a user edit makes, and the same 500 ms accessory refetch (the read-only
 * "To Roof" companion is a server-computed `distance_to_roof` and would
 * otherwise sit stale), but WITHOUT `markUserAction()` on the accessory model.
 * Arming that gate too would also unlock later automatic saves of the door
 * itself — the viewport catalogue auto-picking its last remaining match, say —
 * which is exactly what the gate is for. Only the position model is armed.
 *
 * Re-runs whenever the mounting plane, the clearance values or `y` itself
 * change (plane switch, construction-class change, the mounting popup's
 * distance_to_floor, a websocket echo). `lastAttempt` makes a server that keeps
 * echoing an under-minimum `y` cost one PATCH instead of looping, and clears as
 * soon as the value is legal so a later under-minimum write is corrected too.
 */
export function useAccessDoorFloorMinimum(
  api: AccessoryFormApi,
  planeValue: string | null
): number | null {
  const floorMin = minToFloorClearance(api.model?.get('clearances'), planeValue);
  const writeAllowed = useBoardWriteAccess();

  const apiRef = useRef(api);
  apiRef.current = api;
  const lastAttempt = useRef<string | null>(null);

  const position = api.position;
  const positionId = position ? position.id : null;
  const hasPlane = !!position?.get('mounting_plane_id');
  const rawY = position?.get('y');
  const y = rawY == null || rawY === '' ? null : Number(rawY);
  // a non-unique partner door has no minimum (and renders no form body).
  const nonUnique = api.model?.get('unique') === false;

  useEffect(() => {
    if (api.loading || nonUnique || floorMin == null) return;
    if (positionId == null || !hasPlane || y == null || !Number.isFinite(y)) return;
    if (floorMin - y < SERVER_TOLERANCE) {
      lastAttempt.current = null;
      return;
    }
    // read-only board: show the minimum, never write it.
    if (!writeAllowed) return;
    const token = `${positionId}:${floorMin}:${y}`;
    if (lastAttempt.current === token) return;
    lastAttempt.current = token;
    apiRef.current.commitPositionField('position_y', floorMin.toFixed(2), { positionOnly: true });
  }, [api.loading, nonUnique, floorMin, positionId, hasPlane, y, writeAllowed]);

  return floorMin;
}
